import { Store, createStore, applyMiddleware } from 'redux';
import reduxThunk from 'redux-thunk';
import { combineReducers } from 'redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import RootState from './state';
import audio from './middleware/audio';
import ccp from './middleware/ccp';
import jabra from './middleware/jabra';
import plugins from './middleware/plugins';
import ad from './middleware/ad';
import cognito from './middleware/cognito';
import c4c from './middleware/c4c';
import shortcuts from './middleware/shortcuts';
import okta from './middleware/okta';
import kumodi from './middleware/kumodi';
import parentMessenger from './middleware/parentMessenger';
import internalMessaging from './middleware/internalMessaging';
import logger from './middleware/logger';
import bchat from './middleware/bchat';
import browserNotification from './middleware/browserNotification';

import { getParameterByName } from 'utils';
import authReducer from './auth/auth.reducer';
import callReducer from './call/call.reducer';
import userReducer from './user/user.reducer';
import appReducer from './app/app.reducer';
import contactReducer from './contact/contact.reducer';
import contactsReducer from './contacts/contacts.reducer';
import callLogReducer from './callLog/callLog.reducer';
import chatReducer from './chat/chat.reducer';
import taskReducer from './tasks/tasks.reducer';
import globalReducer from './global/global.reducer';
import agentsReducer from './agents/agents.reducer';
import settingsReducer from './settings/settings.reducer';
import metricsReducer from './metrics/metrics.reducer';
import qaReducer from './qa/qa.reducer';
import internalMessagingReducer from './internal-messaging/internal-messaging.reducer';
import announcementReducer from './announcement/announcement.reducer';
import caseReducer from './case/case.reducer';
import directoryReducer from './directory/directory.reducer';
import campaignManagementReducer from './campaignManagement/campaignManagement.reducer';
import customMessagingReducer from './customMessaging/customMessaging.reducer';
import queuedTasksReducer from './queuedTasks/queuedTasks.reducer';
import notificationReducer from './notification/notification.reducer';
import webHIDReducer from './webHID/webHID.reducer';
import kendraPluginReducer from './kendraPlugin/kendraPlugin.reducer';
import agentsAssistanceReducer from './agentsAssistance/agentsAssistance.reducer';
import contactHistoryReducer from './contactHistory/contactHistory.reducer';
import channelSettingsReducer from './channelSettings/reducer';
import contactSearchReducer from './contactSearch/contactSearch.reducer';
import websocketReducer from './websocket/websocket.reducer';
import websocketCompanionReducer from './websocketCompanion/websocketCompanion.reducer';

const middlewares = [
    reduxThunk,
    ...ccp,
    kumodi,
    audio,
    jabra,
    plugins,
    ad,
    cognito,
    okta,
    shortcuts,
    parentMessenger,
    internalMessaging,
    logger,
    bchat,
    browserNotification,
];

//Add in integration middlewares
if (getParameterByName('integration') === 'c4c') {
    middlewares.push(c4c);
}

export function configureStore(initialState?: RootState): Store<RootState> {
    const middleware = applyMiddleware(...middlewares);

    const rootReducer = combineReducers<RootState>({
        auth: authReducer as any,
        call: callReducer as any,
        chat: chatReducer as any,
        user: userReducer as any,
        app: appReducer as any,
        contact: contactReducer as any,
        contacts: contactsReducer as any,
        callLog: callLogReducer as any,
        global: globalReducer as any,
        agents: agentsReducer as any,
        settings: settingsReducer as any,
        metrics: metricsReducer as any,
        qa: qaReducer as any,
        internalMessaging: internalMessagingReducer as any,
        announcements: announcementReducer as any,
        case: caseReducer as any,
        directory: directoryReducer as any,
        campaignManagement: campaignManagementReducer as any,
        tasks: taskReducer as any,
        customMessaging: customMessagingReducer as any,
        queuedTasks: queuedTasksReducer as any,
        notification: notificationReducer as any,
        webHIDState: webHIDReducer as any,
        kendraPlugin: kendraPluginReducer as any,
        agentsAssistance: agentsAssistanceReducer as any,
        contactHistory: contactHistoryReducer as any,
        channelSettings: channelSettingsReducer as any,
        contactSearch: contactSearchReducer,
        websocket: websocketReducer as any,
        websocketCompanion: websocketCompanionReducer as any,
    });

    const composeEnhancers = composeWithDevTools({ serialize: true });
    const store = createStore(rootReducer, initialState, composeEnhancers(middleware)) as Store<RootState>;

    store.subscribe(() => {
        if (store.getState().app?.error) return;
        const { auth, settings, callLog, metrics } = store.getState();
        const state = {
            //app: store.getState().app,
            auth: {
                logout: auth.logout,
                refreshToken: auth.refreshToken,
            },
            metrics: {
                filters: metrics.filters,
                monitoringAgent: metrics.monitoringAgent,
            },
            callLog: callLog,

            settings: settings
                ? {
                    ringingDevice: settings.ringingDevice,
                    userHasEnabledWebHID: settings.userHasEnabledWebHID
                }
                : undefined,
        };
        localStorage.setItem('state', JSON.stringify(state));
    });
    return store;
}
